import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Hero text reveal animation
export const animateHeroText = (element) => {
  const chars = element.querySelectorAll('.char');
  
  gsap.fromTo(
    chars,
    {
      opacity: 0,
      y: 100,
      rotationX: -90,
    },
    {
      opacity: 1,
      y: 0,
      rotationX: 0,
      stagger: 0.02,
      duration: 1,
      ease: 'power4.out',
    }
  );
};

// Parallax effect
export const createParallax = (element, speed = 0.5) => {
  gsap.to(element, {
    y: () => -ScrollTrigger.maxScroll(window) * speed,
    ease: 'none',
    scrollTrigger: {
      start: 0,
      end: 'max',
      invalidateOnRefresh: true,
      scrub: 0,
    },
  });
};

// Fade in on scroll
export const fadeInOnScroll = (elements, options = {}) => {
  const defaults = {
    y: 100,
    opacity: 0,
    duration: 1,
    stagger: 0.2,
    ease: 'power3.out',
  };

  const settings = { ...defaults, ...options };

  gsap.fromTo(
    elements,
    {
      y: settings.y,
      opacity: settings.opacity,
    },
    {
      y: 0,
      opacity: 1,
      duration: settings.duration,
      stagger: settings.stagger,
      ease: settings.ease,
      scrollTrigger: {
        trigger: elements[0],
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    }
  );
};

// Scale on scroll
export const scaleOnScroll = (element) => {
  gsap.fromTo(
    element,
    {
      scale: 0.8,
      opacity: 0,
    },
    {
      scale: 1,
      opacity: 1,
      duration: 1.5,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 70%',
        toggleActions: 'play none none reverse',
      },
    }
  );
};

// Horizontal scroll
export const createHorizontalScroll = (container, items) => {
  if (!items || items.length === 0) return;
  
  // Convert NodeList to Array if needed
  const itemsArray = Array.from(items);
  const totalWidth = itemsArray.reduce((acc, item) => acc + item.offsetWidth, 0);
  const scrollDistance = totalWidth - window.innerWidth;

  if (scrollDistance <= 0) return;

  gsap.to(itemsArray, {
    x: -scrollDistance,
    ease: 'none',
    scrollTrigger: {
      trigger: container,
      pin: true,
      scrub: 1,
      end: () => `+=${scrollDistance}`,
      invalidateOnRefresh: true,
    },
  });
};

// Split text into characters
export const splitText = (element) => {
  const text = element.textContent;
  element.innerHTML = '';
  
  text.split('').forEach((char) => {
    const span = document.createElement('span');
    span.className = 'char';
    span.style.display = 'inline-block';
    span.textContent = char === ' ' ? '\u00A0' : char;
    element.appendChild(span);
  });
};

// Magnetic effect for elements
export const createMagneticEffect = (element, strength = 0.3) => {
  const handleMouseMove = (e) => {
    const { left, top, width, height } = element.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    const deltaX = (e.clientX - centerX) * strength;
    const deltaY = (e.clientY - centerY) * strength;

    gsap.to(element, {
      x: deltaX,
      y: deltaY,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleMouseLeave = () => {
    gsap.to(element, {
      x: 0,
      y: 0,
      duration: 0.5,
      ease: 'elastic.out(1, 0.3)',
    });
  };

  element.addEventListener('mousemove', handleMouseMove);
  element.addEventListener('mouseleave', handleMouseLeave);

  return () => {
    element.removeEventListener('mousemove', handleMouseMove);
    element.removeEventListener('mouseleave', handleMouseLeave);
  };
};

// Page transition
export const pageTransition = () => {
  const tl = gsap.timeline();
  
  tl.to('.page-transition', {
    scaleY: 1,
    transformOrigin: 'bottom',
    duration: 0.6,
    ease: 'power4.inOut',
  })
  .to('.page-transition', {
    scaleY: 0,
    transformOrigin: 'top',
    duration: 0.6,
    ease: 'power4.inOut',
    delay: 0.2,
  });

  return tl;
};

// Stagger cards
export const staggerCards = (cards) => {
  gsap.fromTo(
    cards,
    {
      y: 100,
      opacity: 0,
      rotationX: -15,
    },
    {
      y: 0,
      opacity: 1,
      rotationX: 0,
      stagger: 0.15,
      duration: 0.8,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: cards[0],
        start: 'top 75%',
        toggleActions: 'play none none reverse',
      },
    }
  );
};

// Reveal from bottom
export const revealFromBottom = (element) => {
  gsap.fromTo(
    element,
    {
      clipPath: 'inset(100% 0 0 0)',
    },
    {
      clipPath: 'inset(0% 0 0 0)',
      duration: 1.2,
      ease: 'power4.out',
      scrollTrigger: {
        trigger: element,
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    }
  );
};

// Counter animation
export const animateCounter = (element, target) => {
  const obj = { val: 0 };
  
  gsap.to(obj, {
    val: target,
    duration: 2,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: element,
      start: 'top 80%',
      once: true,
    },
    onUpdate: () => {
      element.textContent = Math.round(obj.val);
    },
  });
};
